import { useState, useEffect, useCallback } from "react";
import { FileText, Brain, Target, CheckCircle2, Shield, Clock, TrendingUp, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useEdgeFunctionRetry } from "@/hooks/useEdgeFunctionRetry";
import { ErrorRecovery } from "@/components/ui/error-recovery";
import { Progress } from "@/components/ui/progress";
import quentariLogo from "@/assets/quentari-logo.svg";

interface ReportLoadingScreenProps {
  sessionId: string;
  sessionMeta?: Record<string, any> | null;
  committedOptionName?: string;
  alignmentScore?: number;
  isConstraintBreak?: boolean;
  isStrategyShift?: boolean;
  onReportGenerated: (report: any) => void;
  onError: (message: string) => void;
}

const LOADING_STEPS = [
  {
    id: 1,
    label: "Phase-1-Daten konsolidieren",
    description: "Entscheidungsrahmen und Irreversibilität werden zusammengeführt",
    icon: Brain,
  },
  {
    id: 2,
    label: "Matrix-Ergebnisse verdichten",
    description: "Bewertungen, Konflikte und Trade-offs werden strukturiert",
    icon: Target,
  },
  {
    id: 3,
    label: "Dominante Strategie validieren",
    description: "Alignment Score und Constraint-Prüfung werden durchgeführt",
    icon: Shield,
  },
  {
    id: 4,
    label: "Executive Report generieren",
    description: "Der finale Report wird erstellt",
    icon: FileText,
  },
];

const formatEur = (val?: number) => {
  if (val == null) return null;
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val);
};

export const ReportLoadingScreen = ({
  sessionId,
  sessionMeta,
  committedOptionName,
  alignmentScore,
  isConstraintBreak,
  isStrategyShift,
  onReportGenerated,
  onError,
}: ReportLoadingScreenProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<"generating" | "error">("generating");

  const { execute, retry, state: retryState, canRetry } = useEdgeFunctionRetry<any>();

  // Extract session context from meta
  const thesis = sessionMeta?.phase1_4_thesis || sessionMeta?.decision_thesis;
  const irr = sessionMeta?.phase1_3_irreversibility;
  const kernsatz = thesis?.kernsatz;
  const killCriterion = thesis?.killCriterion || thesis?.kill_criterion;
  const urgencyScore = irr?.structural_urgency_score ?? irr?.structuralUrgencyScore;
  const urgencyLevel = urgencyScore != null
    ? (urgencyScore >= 7 ? "red" : urgencyScore >= 4 ? "yellow" : "green")
    : null;
  const dailyExposure = irr?.daily_exposure ?? irr?.dailyExposure;
  const validationDeadlineDays = irr?.validation_deadline_days ?? irr?.validationDeadlineDays;

  const hasContext = !!(kernsatz || committedOptionName || urgencyScore != null);

  const urgencyColor = urgencyLevel === "red"
    ? "text-destructive"
    : urgencyLevel === "yellow"
      ? "text-amber-500"
      : "text-emerald-500";

  const urgencyBg = urgencyLevel === "red"
    ? "bg-destructive/10 border-destructive/20"
    : urgencyLevel === "yellow"
      ? "bg-amber-500/10 border-amber-500/20"
      : "bg-emerald-500/10 border-emerald-500/20";

  // Step progression tied to progress
  useEffect(() => {
    if (progress < 20) setCurrentStep(0);
    else if (progress < 45) setCurrentStep(1);
    else if (progress < 70) setCurrentStep(2);
    else setCurrentStep(3);
  }, [progress]);

  // Progress animation (pauses on retry)
  useEffect(() => {
    if (status !== "generating" || retryState.isRetrying) return;

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 92) return 92;
        return prev + Math.random() * 4;
      });
    }, 600);

    return () => clearInterval(interval);
  }, [status, retryState.isRetrying]);

  const callGenerateReport = useCallback(async () => {
    const requestId = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke("phase3-report", {
      body: { session_id: sessionId, request_id: requestId },
    });
    if (error) throw error;
    if (!data?.report) throw new Error("Report wurde nicht korrekt generiert.");
    return data;
  }, [sessionId]);

  const startGeneration = useCallback(async () => {
    try {
      setStatus("generating");
      setProgress(5);

      const data = await execute(callGenerateReport);

      setProgress(100);
      setCurrentStep(3);

      // Small delay for visual completion
      await new Promise(resolve => setTimeout(resolve, 600));
      onReportGenerated(data.report);
    } catch (error: any) {
      console.error("Report generation error:", error);
      setStatus("error");
      onError(error.message || "Report konnte nicht generiert werden.");
    }
  }, [execute, callGenerateReport, onReportGenerated, onError]);

  const handleManualRetry = useCallback(async () => {
    setStatus("generating");
    setProgress(5);
    try {
      const data = await execute(callGenerateReport);
      setProgress(100);
      await new Promise(resolve => setTimeout(resolve, 600));
      onReportGenerated(data.report);
    } catch (error: any) {
      setStatus("error");
      onError(error.message || "Report konnte nicht generiert werden.");
    }
  }, [execute, callGenerateReport, onReportGenerated, onError]);

  // Start on mount
  useEffect(() => {
    startGeneration();
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-background flex items-center justify-center overflow-y-auto">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.012]"
          style={{
            backgroundImage: `linear-gradient(hsl(var(--primary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
        <div
          className="absolute top-0 left-0 w-full h-full"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 80% 50% at 20% 20%, hsl(var(--primary) / 0.06) 0%, transparent 50%),
              radial-gradient(ellipse 60% 60% at 80% 80%, hsl(var(--primary) / 0.04) 0%, transparent 50%)
            `,
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-8 md:p-10 shadow-2xl animate-fade-in">

          {/* Phase badge */}
          <div className="flex items-center gap-2 mb-8">
            <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Phase 3 abgeschlossen
            </span>
            <span className="mx-1 text-border">·</span>
            <img src={quentariLogo} alt="Quentari" className="h-4 w-auto opacity-60" />
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            Dein Executive Report wird erstellt.
          </h1>
          <p className="text-muted-foreground mb-8">
            Quentari verdichtet deine Analyse, Optionen und Bewertungen in einen entscheidungsreifen Report.
          </p>

          {/* Constraint Warning */}
          {(isConstraintBreak || isStrategyShift) && (
            <div className="mb-6 p-4 rounded-xl border border-amber-500/30 bg-amber-500/10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {isConstraintBreak ? "Constraint-Bruch erkannt" : "Strategiewechsel erkannt"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isConstraintBreak
                      ? "Die committed Option überschreitet die Validierungsdeadline."
                      : "Die committed Option weicht vom Phase-1-Commitment ab."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Session Context Card */}
          {hasContext && (
            <div className="mb-8 p-5 rounded-xl border border-border/40 bg-muted/10">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                Dein Entscheidungsrahmen
              </p>

              {/* Thesis */}
              {kernsatz && (
                <p className="text-base font-semibold text-foreground leading-relaxed mb-3">
                  {kernsatz}
                </p>
              )}

              {/* Kill criterion */}
              {killCriterion && (
                <div className="flex items-start gap-2 text-sm text-muted-foreground mb-4">
                  <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0 text-destructive/70" />
                  <span>Kipppunkt: {killCriterion}</span>
                </div>
              )}

              {/* Committed option + metrics grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {/* Committed option */}
                {committedOptionName && (
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
                      Dominante Option
                    </p>
                    <p className="text-sm font-bold text-foreground truncate">{committedOptionName}</p>
                    {alignmentScore != null && (
                      <p className="text-[10px] text-primary mt-0.5">
                        Alignment {Math.round(alignmentScore * 100)}%
                      </p>
                    )}
                  </div>
                )}

                {/* Urgency */}
                {urgencyScore != null && (
                  <div className={cn("p-3 rounded-lg border", urgencyBg)}>
                    <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
                      Dringlichkeit
                    </p>
                    <p className={cn("text-lg font-bold", urgencyColor)}>{urgencyScore}/10</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {urgencyLevel === "red" ? "Kritisch" : urgencyLevel === "yellow" ? "Eingeschränkt" : "Spielraum"}
                    </p>
                  </div>
                )}

                {/* Validation deadline */}
                {validationDeadlineDays != null && (
                  <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Validierungsfrist</p>
                    </div>
                    <p className="text-lg font-bold text-foreground">{validationDeadlineDays} Tage</p>
                  </div>
                )}

                {/* Daily exposure */}
                {dailyExposure != null && (
                  <div className="p-3 rounded-lg bg-muted/20 border border-border/30">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <TrendingUp className="w-3 h-3 text-muted-foreground" />
                      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Burn / Tag</p>
                    </div>
                    <p className="text-lg font-bold text-foreground">{formatEur(dailyExposure)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error / Retry banner */}
          {(retryState.isRetrying || retryState.isOffline || (status === "error" && retryState.lastError)) && (
            <div className="mb-6">
              <ErrorRecovery
                error={status === "error" ? retryState.lastError : null}
                isRetrying={retryState.isRetrying}
                retryCount={retryState.retryCount}
                isOffline={retryState.isOffline}
                onRetry={handleManualRetry}
              />
            </div>
          )}

          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-foreground">
                {retryState.isRetrying
                  ? `Automatischer Retry ${retryState.retryCount}/3...`
                  : status === "error"
                    ? "Generierung fehlgeschlagen"
                    : LOADING_STEPS[currentStep]?.label}
              </p>
              <span className="text-xs text-muted-foreground">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>

          {/* Steps Timeline */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Verarbeitungsschritte
            </p>
            {LOADING_STEPS.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep && status === "generating";
              const isCompleted = index < currentStep || (index === currentStep && progress >= 100);

              return (
                <div
                  key={step.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-xl border transition-all duration-500",
                    isActive
                      ? "bg-primary/5 border-primary/30 shadow-lg shadow-primary/5"
                      : isCompleted
                        ? "bg-emerald-500/5 border-emerald-500/20"
                        : "bg-muted/30 border-border/50 opacity-50"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all duration-300",
                    isActive
                      ? "bg-primary/15 text-primary"
                      : isCompleted
                        ? "bg-emerald-500/15 text-emerald-500"
                        : "bg-muted text-muted-foreground"
                  )}>
                    {isCompleted ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <Icon className={cn("h-4 w-4", isActive && "animate-pulse")} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm font-medium transition-colors",
                      isActive ? "text-foreground" : isCompleted ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                    )}>
                      {step.label}
                    </p>
                    {isActive && (
                      <p className="text-xs text-muted-foreground mt-0.5 animate-fade-in">
                        {step.description}
                      </p>
                    )}
                  </div>

                  <div className={cn(
                    "text-xs font-bold shrink-0 transition-colors",
                    isActive ? "text-primary" : isCompleted ? "text-emerald-500" : "text-muted-foreground/50"
                  )}>
                    {index + 1}/{LOADING_STEPS.length}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
